/**
 * Languages and countries, as the CRM shows and filters them — 28 Oct 2026.
 *
 * The country is where RevenueCat last saw the account; the language is the
 * one Skida writes in for that country, derived by the engine
 * (`functions-webhook/src/reach.ts`, which this mirrors — grep
 * `languageOfCountry`). Pure, so the composer, the dashboards and the lists
 * all read the same names.
 */

export const LANGUAGES = ['no', 'sv', 'de', 'fr', 'it', 'es', 'en'] as const
export type Language = (typeof LANGUAGES)[number]

export const LANGUAGE_NAMES: Record<Language, string> = {
  no: 'Norsk',
  sv: 'Svenska',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  en: 'English',
}

/** The flag a language is shown with. English is the British one. */
export const LANGUAGE_FLAG: Record<Language, string> = {
  no: 'NO',
  sv: 'SE',
  de: 'DE',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  en: 'GB',
}

const BY_COUNTRY: Record<string, Language> = {
  NO: 'no', SJ: 'no', SE: 'sv', AX: 'sv', DE: 'de', AT: 'de', CH: 'de', LI: 'de',
  FR: 'fr', MC: 'fr', IT: 'it', SM: 'it', VA: 'it', ES: 'es', AD: 'es',
}

/** Mirrors the engine's `languageOfCountry`: anything unlisted is English. */
export function languageOfCountry(country: string | null | undefined): Language | null {
  const c = (country ?? '').trim().toUpperCase()
  if (!c) return null
  return BY_COUNTRY[c] ?? 'en'
}

export const isLanguage = (x: unknown): x is Language =>
  typeof x === 'string' && (LANGUAGES as readonly string[]).includes(x)

/**
 * The countries a section is most often addressed at, first in the picker.
 * Any other ISO code can be typed.
 */
export const COMMON_COUNTRIES = ['NO', 'SE', 'DK', 'FI', 'DE', 'AT', 'CH', 'FR', 'IT', 'ES', 'GB', 'US', 'CA', 'NZ', 'AU']

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' })
  } catch {
    return null
  }
})()

/** "NO" → "Norway"; the code itself when the browser cannot say. */
export const countryName = (code: string): string => {
  try {
    return regionNames?.of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}
